import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-hot-toast';
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_URL, VERIFY_API_URL } from './credentials';

// Re-export API_URL for components that need direct access
export { API_URL };

// Type for localStorage data wrapper
interface StoredData {
  dataType: string;
  data: unknown;
}

// Type for toast messages
type ToastType = 'success' | 'error' | 'info' | 'warning';

// Type for privilege titles
type PrivilegeTitles = Record<string, string>;

export const newID = (): string => {
  return uuidv4();
};

export const getEmployeeImageUrl = (faceUrl?: string): string | undefined => {
  if (!faceUrl) {
    return undefined;
  }
  return `${API_URL}${faceUrl}`;
};

/**
 * Normalizes an image URL to use the current API_URL
 * Handles cases where the URL was stored with a different base URL (e.g., local dev IP)
 */
export const normalizeImageUrl = (url?: string): string | undefined => {
  if (!url) {
    return undefined;
  }

  // If it's already a relative path, prepend API_URL
  if (url.startsWith('/')) {
    return `${API_URL}${url}`;
  }

  // If it contains a different base URL, extract the path and use current API_URL
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;
    return `${API_URL}${path}`;
  } catch {
    // If URL parsing fails, return as-is
    return url;
  }
};

export const saveData = (key: string, value: unknown): void => {
  const payload: StoredData = {
    dataType: typeof value,
    data: value,
  };

  localStorage.setItem(key, JSON.stringify(payload));
};

export const ApiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

export const VerifyApiClient: AxiosInstance = axios.create({
  baseURL: VERIFY_API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Add request interceptor for debugging and required headers
ApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add system_user_id header required by backend
    const profileData = getData('profileData') as any;
    if (profileData?.userId) {
      config.headers['system_user_id'] = String(profileData.userId);
    }

    // Logging disabled - use external monitoring tools in production
    // if (process.env.REACT_APP_ENABLE_LOGGING === 'true') {
    //   console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    // }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
ApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (!error.response) {
      // Network error - likely CORS or server not running
      console.error('Network Error: Cannot reach the server. Please check:');
      console.error('1. Backend server is running at:', API_URL);
      console.error('2. CORS is properly configured on the backend');
      console.error('3. Your network connection');

      toast.error('Cannot connect to server. Please check your connection.', {
        style: { background: '#ef4444', color: '#fff' },
        duration: 5000,
      });
    } else if (error.response.status === 401) {
      // Unauthorized - redirect to login
      console.error('🚨 401 Unauthorized Error:', {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        currentPath: window.location.pathname,
      });

      // Don't redirect if we're already on login page
      if (window.location.pathname === '/login' || window.location.pathname === '/') {
        return Promise.reject(error);
      }

      localStorage.removeItem('token');
      localStorage.removeItem('profileData');
      delete ApiClient.defaults.headers.common['Authorization'];
      window.location.replace('/login');
    } else if (error.response.status === 403) {
      toast.error('Access forbidden. You do not have permission.', {
        style: { background: '#ef4444', color: '#fff' },
      });
    } else if (error.response.status >= 500) {
      toast.error('Server error. Please try again later.', {
        style: { background: '#ef4444', color: '#fff' },
      });
    }

    return Promise.reject(error);
  }
);

// Same interceptors for VerifyApiClient
VerifyApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (!error.response) {
      console.error('Network Error: Cannot reach verification server at:', VERIFY_API_URL);
    }
    return Promise.reject(error);
  }
);

export const getData = (key: string): unknown => {
  const result = localStorage.getItem(key);
  if (!result) {
    return null;
  }
  try {
    const parsed: StoredData = JSON.parse(result);
    return parsed.data;
  } catch (error) {
    console.warn(`Failed to parse localStorage item for key "${key}":`, error);
    return null;
  }
};

export const newToast = (type: ToastType, message: string): void => {
  const colors: Record<ToastType, { background: string; color: string }> = {
    success: { background: '#22c55e', color: '#fff' },
    error: { background: '#ef4444', color: '#fff' },
    info: { background: '#3b82f6', color: '#fff' },
    warning: { background: '#facc15', color: '#000' },
  };
  const style = colors[type] || { background: '#9ca3af', color: '#fff' };
  toast(message, {
    style: {
      padding: '12px 20px',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '14px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      ...style,
    },
    duration: 3000,
  });
};

export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || error.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const fullDateTime = (value?: string | Date): string => {
  let date: Date;
  if (value) {
    date = new Date(value);
  } else {
    date = new Date();
  }

  const hh = date.getUTCHours().toString().padStart(2, '0');
  const mm = (date.getUTCMinutes() + 1).toString().padStart(2, '0');

  return `${date.toDateString()} - Time ${hh}:${mm}`;
};

export const fullDate = (value?: string | Date): string => {
  let date: Date;
  if (value) {
    date = new Date(value);
  } else {
    date = new Date();
  }

  // Using built-in date formatting
  return date.toDateString();
};

export const fullTime = (value?: string | Date): string => {
  const date = value ? new Date(value) : new Date();

  const hh = String(date.getUTCHours()).padStart(2, '0');
  const min = String(date.getUTCMinutes()).padStart(2, '0');
  const ss = String(date.getUTCSeconds()).padStart(2, '0');

  return `${hh}:${min}:${ss} UTC`;
};

export const convertTo12HourFormat = (timeString?: string): string => {
  if (!timeString) {
    return '';
  }

  // Split HH:mm:ss
  const parts = timeString.split(':');
  const hourStr = parts[0];
  const minuteStr = parts[1];

  if (!hourStr || !minuteStr) {
    return timeString; // fallback if format is invalid
  }

  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (isNaN(hour) || isNaN(minute)) {
    return timeString;
  } // fallback

  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12; // convert 0 â†’ 12 and 13â€“23 â†’ 1â€“11

  return `${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;
};

export const shortenText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  const shortened = `${text.substring(0, maxLength - 3)}...`;
  return shortened;
};

export const DevFullDate = (value?: string | Date): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  return `${yyyy}-${mm}-${dd}`; // <-- format for <input type="date">
};

export function getHoursBetween(start_time: string, end_time: string): number {
  const startParts = start_time.split(':').map(Number);
  const endParts = end_time.split(':').map(Number);

  const startH = startParts[0] ?? 0;
  const startM = startParts[1] ?? 0;
  const startS = startParts[2] ?? 0;
  const endH = endParts[0] ?? 0;
  const endM = endParts[1] ?? 0;
  const endS = endParts[2] ?? 0;

  const startSeconds = startH * 3600 + startM * 60 + startS;
  let endSeconds = endH * 3600 + endM * 60 + endS;
  // Handle crossing midnight (e.g. 22:00:00 â†’ 02:00:00)
  if (endSeconds < startSeconds) {
    endSeconds += 24 * 3600;
  }
  const diffSeconds = endSeconds - startSeconds;
  const diffHours = diffSeconds / 3600;
  return parseFloat(diffHours.toFixed(2));
}

export const privilegeTitles: PrivilegeTitles = {
  // Employee Management
  'add-update-department': 'Add or Update Department',
  'delete-department': 'Delete Department',
  'get-departments': 'View Departments',

  'add-update-role': 'Add or Update Role',
  'delete-role': 'Delete Role',
  'get-roles': 'View Roles',

  'add-update-employee': 'Add or Update Employee',
  'delete-employee': 'Delete Employee',
  'get-employee': 'View Employees',

  // Client Management
  'add-update-client-category': 'Add or Update Client Category',
  'delete-client-category': 'Delete Client Category',
  'get-client-category': 'View Client Categories',

  'add-update-client': 'Add or Update Client',
  'get-clients': 'View Clients',
  'delete-client': 'Delete Client',

  // Security Operations â†’ Manage Services
  'add-update-service': 'Add or Update Service',
  'delete-service': 'Delete Service',
  'get-services': 'View Services',

  // Security Operations â†’ Client Services
  'add-update-client-service': 'Add or Update Client Service',
  'delete-client-service': 'Delete Client Service',
  'get-client-services': 'View Client Services',

  // Security Operations â†’ Site Types
  'add-update-site-type': 'Add or Update Site Type',
  'delete-site-type': 'Delete Site Type',
  'get-site-types': 'View Site Types',

  // Security Operations â†’ Sites
  'add-update-site': 'Add or Update Site',
  'delete-site': 'Delete Site',
  'get-sites': 'View Sites',
  'get-all-sites': 'View All Sites',
  'get-zone-sites': 'View Zone Sites',

  'save-site-location': 'Update Site Location',
  'get-user-sites': 'View User Sites',
  'add-office-location': 'Add Office Location',
  'get-office-locations': 'View Office Locations',

  // Security Operations â†’ Zones
  'add-update-zone': 'Add or Update Zone',
  'delete-zone': 'Delete Zone',
  'get-zones': 'View Zones',

  // Security Operations â†’ Guard Operations - Assign Guard
  'add-update-assign-guard': 'Assign Guard',
  'get-assign-guards': 'View Guard Assignments',
  'delete-assign-guard': 'Delete Guard Assignment',

  // Security Operations â†’ Guard Operations - Shifts
  'add-update-shift': 'Add or Update Shift',
  'delete-shift': 'Delete Shift',
  'get-shifts': 'View Shifts',

  // Security Operations â†’ Guard Operations - Supervisor
  'add-update-supervisor': 'Assign Supervisor',
  'get-assign-supervisors': 'View Supervisor Assignments',
  'delete-assign-supervisor': 'Delete Supervisor Assignment',

  // Security Operations â†’ Metrics
  'get-total-guards-on-duty': 'View Total Guards on Duty',

  // Security Operations â†’ Clockin
  'get-clockin-data': 'View Clockin Data',

  // Admin
  'add-update-user': 'Add or Update Admin User',
  'get-user': 'View Users',
  'get-session': 'View Login Sessions',
};
